import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Building2, MapPin, FileText, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import SPShell from "@/components/SPShell";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUS_STYLE = {
  NEW: { bg: "var(--blue-light)", color: "var(--blue-dark)" },
  VIEWED: { bg: "#fef3c7", color: "#92400e" },
  RESPONDED: { bg: "#dcfce7", color: "#166534" },
  QUOTED: { bg: "#dcfce7", color: "#166534" },
  DECLINED: { bg: "#fee2e2", color: "#991b1b" },
  CLOSED: { bg: "#e2e8f0", color: "#475569" },
};

const RESPONSE_STATUSES = [
  { value: "RESPONDED", label: "Responded (info provided)" },
  { value: "QUOTED", label: "Quoted (formal offer attached)" },
  { value: "DECLINED", label: "Declined (cannot service)" },
  { value: "CLOSED", label: "Closed (no further action)" },
];

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.NEW;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider"
      style={{ background: s.bg, color: s.color }}
      data-testid="sp-lead-detail-status"
    >
      {status}
    </span>
  );
}

export default function SPLeadDetail() {
  const { lead_id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState("RESPONDED");
  const [summary, setSummary] = useState("");
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/sp/leads/${lead_id}`);
      setLead(data);
      // Mark as viewed if NEW (fire-and-forget)
      if (data.status === "NEW") {
        api.post(`/sp/leads/${lead_id}/view`).catch(() => {});
      }
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead_id]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!summary.trim() && status !== "DECLINED" && status !== "CLOSED") {
      toast.error("Add a short response summary before sending.");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        status,
        response_summary: attachmentUrl.trim()
          ? `${summary.trim()}\n\nAttachment: ${attachmentUrl.trim()}`
          : summary.trim(),
      };
      await api.post(`/sp/leads/${lead_id}/respond`, body);
      toast.success(`Lead marked ${status}. The chair has been notified.`);
      await load();
      setSummary("");
      setAttachmentUrl("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const original = lead?.original_quote_request;
  const canRespond = lead && !["CLOSED"].includes(lead.status);

  return (
    <SPShell>
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-8" data-testid="sp-lead-detail">
        <Link to="/sp/leads">
          <Button variant="ghost" size="sm" className="h-8 mb-4 -ml-2" data-testid="sp-lead-back">
            <ArrowLeft size={14} className="mr-1.5" />
            Back to leads
          </Button>
        </Link>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-8 w-2/3" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : error ? (
          <div
            className="rounded-lg p-4 text-sm"
            style={{ background: "var(--alert-light)", color: "var(--alert)" }}
            data-testid="sp-lead-detail-error"
          >
            <AlertCircle size={16} className="inline mr-2" />
            {error}
          </div>
        ) : lead ? (
          <>
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-3 flex-wrap mb-1">
                <h1
                  className="text-2xl font-bold tracking-tight"
                  style={{ color: "var(--navy)" }}
                  data-testid="sp-lead-detail-heading"
                >
                  {lead.scheme_name || "Scheme lead"}
                </h1>
                <StatusPill status={lead.status} />
              </div>
              <div className="text-sm" style={{ color: "var(--slate)" }}>
                Source:{" "}
                <span className="font-medium" style={{ color: "var(--navy)" }}>
                  {lead.source === "insurance_quote_request" ? "Insurance quote request" : lead.source}
                </span>
                {" · received "}
                {new Date(lead.submitted_at).toLocaleString()}
              </div>
            </div>

            {/* Scheme card */}
            <div
              className="rounded-xl bg-white border p-5 mb-5"
              style={{ borderColor: "var(--border)" }}
              data-testid="sp-lead-scheme-card"
            >
              <div className="flex items-center gap-2 mb-3">
                <Building2 size={16} style={{ color: "var(--blue)" }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--slate)" }}>
                  Scheme
                </span>
              </div>
              <div className="text-base font-semibold" style={{ color: "var(--navy)" }}>
                {lead.scheme?.name || lead.scheme_name}
              </div>
              {lead.scheme?.state && (
                <div className="text-xs mt-0.5 inline-block px-2 py-0.5 rounded mt-1"
                  style={{ background: "var(--blue-light)", color: "var(--blue-dark)" }}
                >
                  {lead.scheme.state}
                </div>
              )}
              {lead.scheme?.address && (
                <div className="text-sm mt-2 flex items-start gap-1.5" style={{ color: "var(--slate)" }}>
                  <MapPin size={13} className="mt-0.5 shrink-0" />
                  <span>{lead.scheme.address}</span>
                </div>
              )}
            </div>

            {/* Original quote request */}
            {original && (
              <div
                className="rounded-xl bg-white border p-5 mb-5"
                style={{ borderColor: "var(--border)" }}
                data-testid="sp-lead-original-request"
              >
                <div className="flex items-center gap-2 mb-3">
                  <FileText size={16} style={{ color: "var(--blue)" }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--slate)" }}>
                    Original quote request
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {[
                    ["Contact name", original.contact_name],
                    ["Contact email", original.contact_email],
                    ["Contact phone", original.contact_phone],
                    ["Sum insured", original.sum_insured && `$${Number(original.sum_insured).toLocaleString()}`],
                    ["Current insurer", original.current_insurer],
                    ["Renewal date", original.renewal_date],
                  ].map(([k, v]) =>
                    v ? (
                      <div key={k}>
                        <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--slate)" }}>
                          {k}
                        </div>
                        <div className="font-medium" style={{ color: "var(--navy)" }}>
                          {v}
                        </div>
                      </div>
                    ) : null
                  )}
                </div>
                {original.notes && (
                  <div className="mt-4">
                    <div className="text-[11px] uppercase tracking-wider font-semibold mb-1" style={{ color: "var(--slate)" }}>
                      Notes
                    </div>
                    <div
                      className="text-sm p-3 rounded-md whitespace-pre-wrap"
                      style={{ background: "#f8fafc", color: "var(--navy)" }}
                    >
                      {original.notes}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Existing response (if already responded) */}
            {lead.response_summary && (
              <div
                className="rounded-xl border p-5 mb-5"
                style={{ borderColor: "var(--border)", background: "#f8fafc" }}
                data-testid="sp-lead-existing-response"
              >
                <div className="text-[11px] uppercase tracking-wider font-semibold mb-2" style={{ color: "var(--slate)" }}>
                  Your last response
                  {lead.responded_at && (
                    <span className="ml-2 normal-case font-normal">
                      ({new Date(lead.responded_at).toLocaleString()})
                    </span>
                  )}
                </div>
                <div
                  className="text-sm whitespace-pre-wrap"
                  style={{ color: "var(--navy)" }}
                >
                  {lead.response_summary}
                </div>
              </div>
            )}

            {/* Respond form */}
            {canRespond && (
              <form
                onSubmit={onSubmit}
                className="rounded-xl bg-white border p-5"
                style={{ borderColor: "var(--border)" }}
                data-testid="sp-lead-respond-form"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Send size={16} style={{ color: "var(--blue)" }} />
                  <h2 className="text-sm font-semibold" style={{ color: "var(--navy)" }}>
                    {lead.response_summary ? "Send another response" : "Respond to this lead"}
                  </h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="sp-status" className="text-xs font-semibold mb-1.5 block">
                      Status
                    </Label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger id="sp-status" data-testid="sp-respond-status-trigger">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {RESPONSE_STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value} data-testid={`sp-respond-status-${s.value}`}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="sp-summary" className="text-xs font-semibold mb-1.5 block">
                      Response summary <span className="font-normal text-slate-500">(markdown supported)</span>
                    </Label>
                    <Textarea
                      id="sp-summary"
                      value={summary}
                      onChange={(e) => setSummary(e.target.value)}
                      rows={6}
                      placeholder="Briefly summarise your response, quote, or next steps. The chair receives this verbatim via email."
                      data-testid="sp-respond-summary"
                    />
                  </div>

                  <div>
                    <Label htmlFor="sp-attachment" className="text-xs font-semibold mb-1.5 block">
                      Attachment URL <span className="font-normal text-slate-500">(optional — e.g. PDF quote link)</span>
                    </Label>
                    <Input
                      id="sp-attachment"
                      type="url"
                      value={attachmentUrl}
                      onChange={(e) => setAttachmentUrl(e.target.value)}
                      placeholder="https://..."
                      data-testid="sp-respond-attachment"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full sm:w-auto"
                    data-testid="sp-respond-submit"
                    style={{ background: "var(--blue)", color: "white" }}
                  >
                    {submitting ? "Sending…" : `Send response (${status})`}
                  </Button>
                  <p className="text-[11px]" style={{ color: "var(--slate)" }}>
                    Sending will update the lead status and email the scheme chair with your summary.
                  </p>
                </div>
              </form>
            )}

            {!canRespond && (
              <div
                className="rounded-lg p-4 text-sm"
                style={{ background: "#f1f5f9", color: "var(--slate)" }}
                data-testid="sp-lead-closed-notice"
              >
                This lead is closed. No further responses can be sent.
              </div>
            )}
          </>
        ) : null}
      </div>
    </SPShell>
  );
}
